
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, MessageSquareWarning, Code, FileCode, TestTube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MainNav from '@/components/MainNav';
import AccessibilityWidget from '@/components/AccessibilityWidget';
import DeveloperDocs from '@/components/DeveloperDocs';
import TechnicalReference from '@/components/TechnicalReference';
import TroubleshootingGuide from '@/components/TroubleshootingGuide';

const DeveloperDocsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <AccessibilityWidget />
      <header>
        <MainNav currentPath="docs" />
      </header>
      <main className="container py-8">
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
            </Link>
          </Button>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
                Developer Documentation
              </h1>
              <p className="text-muted-foreground text-base md:text-lg">
                Comprehensive guides for implementing and customizing Accesstopia.
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/installation" className="flex items-center gap-1">
                  <Code className="h-4 w-4" /> Installation Guide
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/installation/test" className="flex items-center gap-1">
                  <TestTube className="h-4 w-4" /> Test Installation
                </Link>
              </Button>
            </div>
          </div>
        </div>
        
        <Tabs defaultValue="guides" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="guides" className="flex items-center gap-1">
              <BookOpen className="h-4 w-4" />
              Integration Guides
            </TabsTrigger>
            <TabsTrigger value="reference" className="flex items-center gap-1">
              <FileCode className="h-4 w-4" />
              Technical Reference
            </TabsTrigger>
            <TabsTrigger value="troubleshooting" className="flex items-center gap-1">
              <MessageSquareWarning className="h-4 w-4" />
              Troubleshooting
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="guides" className="py-6">
            <DeveloperDocs />
          </TabsContent>
          
          <TabsContent value="reference" className="py-6">
            <TechnicalReference />
          </TabsContent>
          
          <TabsContent value="troubleshooting" className="py-6">
            <TroubleshootingGuide />
          </TabsContent>
        </Tabs>
      </main>
      
      <footer className="border-t py-6 px-4 bg-background">
        <div className="container max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Accesstopia. All rights reserved.
          </p>
          <div className="flex items-center gap-6 mt-4 md:mt-0">
            <div className="flex gap-6">
              <Link to="/" className="text-muted-foreground hover:text-foreground text-sm">
                Home
              </Link>
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground text-sm">
                Pricing
              </Link>
              <Link to="/installation" className="text-muted-foreground hover:text-foreground text-sm">
                Installation
              </Link>
              <Link to="/demo" className="text-muted-foreground hover:text-foreground text-sm">
                Demo
              </Link>
            </div>
            <a 
              href="https://www.mattblak.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs text-muted-foreground hover:text-foreground ml-4"
            >
              Powered by Matt Blak
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DeveloperDocsPage;
